import React from 'react';
import { useSelector } from 'react-redux';
import {
  FiArrowLeft,
  FiBookOpen,
  FiCalendar,
  FiCheck,
  FiChevronRight,
  FiClock,
  FiLayers,
  FiUser,
  FiX,
  FiZap,
} from 'react-icons/fi';
import CourseRegistrationPanel from '../components/CourseRegistrationPanel';
import DigitalLmsStudyViewer from '../components/DigitalLmsStudyViewer';
import LearningActions from '../components/LearningActions';
import LmsMainDetail from '../components/LmsMainDetail';
import useDigitalLmsData from '../hooks/useDigitalLmsData';
import './DesktopDigitalLms.scss';

const DesktopTopbar = ({ onClose, userName }) => (
  <header className="desktop-lms-topbar">
    <div className="desktop-lms-topbar__inner">
      <div className="desktop-lms-brand" aria-label="K-EDU 디지털 LMS">
        <span>K</span>
        <div>
          <strong>Digital LMS</strong>
          <small>나의 온라인 학습실</small>
        </div>
      </div>

      <div className="desktop-lms-user">
        <span><FiUser aria-hidden="true" /></span>
        <div>
          <small>학습자</small>
          <strong>{userName}</strong>
        </div>
        <button type="button" onClick={onClose} aria-label="서비스 메뉴로 이동" title="닫기">
          <FiX aria-hidden="true" />
        </button>
      </div>
    </div>
  </header>
);

const DesktopLearningDetail = ({ lesson, onBack, onClose, userName }) => {
  const isStudyViewerOpen = useSelector((state) => state.reduxStore.isModalTestLms);

  return (
    <main className={`desktop-digital-lms is-detail${isStudyViewerOpen ? ' is-study-viewer-open' : ''}`} aria-label="PC 디지털 LMS 학습 상세">
      <DesktopTopbar onClose={onClose} userName={userName} />
      <div className="desktop-lms-detail-shell">
        <header className="desktop-lms-detail-header">
          <button type="button" onClick={onBack} aria-label="학습 목록으로 돌아가기">
            <FiArrowLeft aria-hidden="true" />
          </button>
          <div>
            <span>학습 상세</span>
            <h1>{lesson.title}</h1>
            <p>
              <FiCalendar aria-hidden="true" /> {lesson.date}
              <i />
              <FiUser aria-hidden="true" /> {lesson.writer || '담당 선생님'}
            </p>
          </div>
        </header>
        <div className="desktop-lms-detail-body">
          <LmsMainDetail sno={lesson.sno} showHeader={false} />
        </div>
      </div>
      <DigitalLmsStudyViewer device="desktop" />
    </main>
  );
};

const DesktopDigitalLms = ({ onClose }) => {
  const isStudyViewerOpen = useSelector((state) => state.reduxStore.isModalTestLms);
  const {
    allClasses,
    closeLesson,
    courseGroups,
    isCourseLoading,
    isLessonLoading,
    lessons,
    newLessonCount,
    openLesson,
    selectClass,
    selectedClass,
    selectedLesson,
    userName,
  } = useDigitalLmsData();

  if (selectedLesson) {
    return (
      <DesktopLearningDetail
        lesson={selectedLesson}
        onBack={closeLesson}
        onClose={onClose}
        userName={userName}
      />
    );
  }

  return (
    <main className={`desktop-digital-lms${isStudyViewerOpen ? ' is-study-viewer-open' : ''}`} aria-label="PC 디지털 LMS">
      <DesktopTopbar onClose={onClose} userName={userName} />

      <div className="desktop-lms-scroll">
        <div className="desktop-lms-container">
          <section className="desktop-lms-hero">
            <div className="desktop-lms-hero__copy">
              <span><FiZap aria-hidden="true" /> 오늘의 학습</span>
              <h1>{userName}님, 오늘도 배움의 흐름을 이어가세요.</h1>
              <p>수업을 선택하고 새로 등록된 학습과 과제를 한곳에서 확인할 수 있습니다.</p>
            </div>

            <div className="desktop-lms-hero__stats" aria-label="학습 현황">
              <article>
                <span><FiLayers aria-hidden="true" /></span>
                <div><small>수강 수업</small><strong>{allClasses.length}</strong></div>
              </article>
              <article>
                <span><FiBookOpen aria-hidden="true" /></span>
                <div><small>학습 자료</small><strong>{lessons.length}</strong></div>
              </article>
              <article>
                <span><FiZap aria-hidden="true" /></span>
                <div><small>새 학습</small><strong>{newLessonCount}</strong></div>
              </article>
            </div>
            <LearningActions classCode={selectedClass?.code} />
          </section>

          <div className="desktop-lms-workspace">
            <aside className="desktop-lms-sidebar">
              <header className="desktop-lms-section-heading">
                <div>
                  <h2>내 수업</h2>
                </div>
                <em>{allClasses.length}</em>
              </header>

              <CourseRegistrationPanel className="desktop-lms-registration" />

              <div className="desktop-lms-course-list">
                {isCourseLoading && (
                  <div className="desktop-lms-course-loading" aria-label="수업 목록 불러오는 중">
                    <i /><i /><i />
                  </div>
                )}

                {!isCourseLoading && courseGroups.length === 0 && (
                  <div className="desktop-lms-course-empty">
                    <FiLayers aria-hidden="true" />
                    <strong>수강 중인 수업이 없습니다.</strong>
                    <span>위 수강신청 버튼으로 수업을 추가해 보세요.</span>
                  </div>
                )}

                {!isCourseLoading && courseGroups.map((group) => (
                  <section className="desktop-lms-course-group" key={group.code}>
                    <h3>{group.name}<span>{group.classes.length}</span></h3>
                    {group.classes.map((classItem) => {
                      const isSelected = String(classItem.code) === String(selectedClass?.code);
                      return (
                        <button
                          type="button"
                          className={isSelected ? 'is-selected' : ''}
                          onClick={() => selectClass(classItem)}
                          key={classItem.code}
                        >
                          <span className="desktop-lms-course-group__icon">
                            {isSelected ? <FiCheck aria-hidden="true" /> : <FiBookOpen aria-hidden="true" />}
                          </span>
                          <span className="desktop-lms-course-group__name">
                            <strong>{classItem.name}</strong>
                            <small>{group.name}</small>
                          </span>
                          <FiChevronRight aria-hidden="true" />
                        </button>
                      );
                    })}
                  </section>
                ))}
              </div>
            </aside>

            <section className="desktop-lms-lessons">
              <header className="desktop-lms-lessons__header">
                <div>
                  <h2>{selectedClass?.name || '학습 목록'}</h2>
                  <p>{selectedClass ? '최근 등록된 학습부터 확인하세요.' : '왼쪽에서 수업을 선택해 주세요.'}</p>
                </div>
                <em>{lessons.length}개</em>
              </header>

              {isLessonLoading && (
                <div className="desktop-lms-lesson-grid is-loading" aria-label="학습 목록 불러오는 중">
                  {[0, 1, 2, 3].map((item) => <i key={item} />)}
                </div>
              )}

              {!isLessonLoading && lessons.length === 0 && (
                <div className="desktop-lms-lessons-empty">
                  <span><FiBookOpen aria-hidden="true" /></span>
                  <strong>{selectedClass ? '아직 등록된 학습이 없습니다.' : '수업을 선택해 주세요.'}</strong>
                  <p>{selectedClass ? '새로운 학습이 등록되면 이곳에 표시됩니다.' : '수업별 학습 자료와 과제를 확인할 수 있습니다.'}</p>
                </div>
              )}

              {!isLessonLoading && lessons.length > 0 && (
                <div className="desktop-lms-lesson-grid">
                  {lessons.map((lesson) => {
                    const [, month, day] = String(lesson.date || '').split('-');
                    return (
                      <button
                        type="button"
                        className="desktop-lms-lesson-card"
                        key={lesson.sno || lesson.code}
                        onClick={() => openLesson(lesson)}
                      >
                        <span className="desktop-lms-lesson-card__date">
                          <small>{month ? `${Number(month)}월` : '학습'}</small>
                          <strong>{day ? Number(day) : '-'}</strong>
                        </span>
                        <span className="desktop-lms-lesson-card__body">
                          <span>
                            <small><FiCalendar aria-hidden="true" /> {lesson.date || '날짜 미정'}</small>
                          </span>
                          <strong>{lesson.title}</strong>
                          <small><FiUser aria-hidden="true" /> {lesson.writer || '담당 선생님'}</small>
                        </span>
                        <span className="desktop-lms-lesson-card__action">
                          학습 보기 <FiChevronRight aria-hidden="true" />
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          <footer className="desktop-lms-footer">
            <FiClock aria-hidden="true" /> 학습 내용과 과제는 담당 선생님이 등록한 순서대로 제공됩니다.
          </footer>
        </div>
      </div>

      <DigitalLmsStudyViewer device="desktop" />
    </main>
  );
};

export default DesktopDigitalLms;
